import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Tables } from '../lib/database.types';

const QUERY_KEY = 'experiences';

export const useExperiences = (options = {}) => {
  const { language = 'fr' } = options;

  return useQuery({
    queryKey: [QUERY_KEY, { language }],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(Tables.EXPERIENCES)
        .select(`
          *,
          translations:experiences_translations(role, description, language_code),
          technologies:experience_technologies(
            technology:technologies(name)
          ),
          skills:experience_skills(
            skill:skills(
              id,
              translations:skills_translations(name, language_code)
            )
          )
        `)
        .order('is_current', { ascending: false })
        .order('start_date', { ascending: false });

      if (error) throw error;

      return data.map(experience => ({
        ...experience,
        role: experience.translations?.find(t => t.language_code === language)?.role || experience.translations?.[0]?.role,
        description: experience.translations?.find(t => t.language_code === language)?.description || experience.translations?.[0]?.description,
        technologies: experience.technologies?.map(et => et.technology?.name).filter(Boolean) || [],
        skills: experience.skills?.map(es => {
          const trans = es.skill?.translations?.find(t => t.language_code === language) || es.skill?.translations?.[0];
          return trans?.name;
        }).filter(Boolean) || []
      }));
    },
  });
};

export const useCreateExperience = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (experience) => {
      const { data, error } = await supabase
        .from(Tables.EXPERIENCES)
        .insert(experience)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateExperience = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from(Tables.EXPERIENCES)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useDeleteExperience = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from(Tables.EXPERIENCES)
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export default useExperiences;
